import React, { useRef } from 'react';
import dayjs from 'dayjs';
import { Calendar } from 'primereact/calendar';
import { useTranslation } from 'react-i18next';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { longDateFormat } from '@utils/dateWithTz';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgPlus from '@icons/plus.svg?react';
import SvgCalendar from '@icons/calendar.svg?react';
import SvgChevron from '@icons/chevron.svg?react';
import SvgPlan from '@icons/plan.svg?react';
import { Title } from './Title';

/** Shared shape of the header's bordered controls: 32px tall, 1px border, square-ish corners. */
const CONTROL_CLASS = 'h-8 border border-content-tertiary py-0 shadow-none';

interface Props {
  currentDate: string;
  onDateChange: (date: string) => void;
  onNewReservation: () => void;
  onCustomise: () => void;
}

/**
 * Page header (Figma "PA Headline", node 20314:584985): title, a segmented date stepper
 * (‹ | date | ›) with a separate "Today" button, the "new reservation" action, and the calendar
 * settings on the right.
 */
export const CalendarToolbar: React.FC<Props> = ({ currentDate, onDateChange, onNewReservation, onCustomise }) => {
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const pickerRef = useRef<any>(null);
  /** Whether the date overlay was open when the current click started — see the toggle below. */
  const overlayWasOpen = useRef(false);

  const currentDay = dayjs(currentDate);

  /**
   * The date input is typable, so half-finished or nonsense input arrives here as null or an
   * Invalid Date. Ignore those instead of navigating — writing one into `currentDate` would poison
   * every consumer of it (the fetch range, the stepper arithmetic, the picker's own value).
   */
  const handlePickedDate = (picked: Date | null) => {
    const day = dayjs(picked);
    if (!picked || !day.isValid()) return;
    onDateChange(day.format());
  };

  return (
    <div className="flex items-center justify-between pt-4 pb-2">
      <div className="flex items-center gap-6">
        <Title className="whitespace-nowrap text-[1.625rem] tracking-[-0.02em]">{tr(`calendar.reservation`, `Reservation`)}</Title>

        <div className="flex h-8 items-center gap-2">
          {/* Stepper segments overlap by 1px so neighbours share a single border line. */}
          <div className="flex h-full items-center">
            <RingilButton
              mode="table"
              icon={<SvgChevron className="h-4 w-4 rotate-90" />}
              onClick={() => onDateChange(dayjs(currentDate).subtract(1, 'days').format())}
              tooltip={tr('TimeslotReservations.PreviousDay', 'Previous day')}
              className={`${CONTROL_CLASS} -mr-px rounded-none rounded-l px-3`}
              dataTestId="calendar-prev-day"
            />

            {/* PrimeReact's Calendar takes no onKeyDown and only closes on Escape, so Enter is handled
                on the wrapper: it confirms whatever parsed (invalid input was already ignored above)
                and closes the overlay. */}
            <div
              className="-mr-px inline-flex"
              // Toggle on click. The state has to be sampled on mousedown: by the time `click` fires,
              // focus has already opened the overlay, so "is it open now?" is always true.
              onMouseDown={() => {
                overlayWasOpen.current = !!pickerRef.current?.isVisible?.();
              }}
              onClick={e => {
                if (!overlayWasOpen.current) return;
                // The overlay panel is rendered inside this wrapper, so its own clicks (month arrows,
                // year dropdowns) bubble here too — those must not be read as "toggle the field".
                if ((e.target as HTMLElement).closest('.p-datepicker')) return;
                pickerRef.current?.hideOverlay?.();
                e.currentTarget.querySelector('input')?.blur();
              }}
              onKeyDown={e => {
                if (e.key !== 'Enter') return;
                // Enter on the panel's own controls (month arrows, year buttons) must operate them,
                // not confirm-and-close the field.
                if ((e.target as HTMLElement).closest('.p-datepicker')) return;
                e.preventDefault();
                pickerRef.current?.hideOverlay?.();
                // The Calendar opens on focus, and hiding leaves the input focused — a following click
                // would fire no new focus event and the picker would stay shut. Blur so the next click
                // lands on an unfocused field and reopens it.
                e.currentTarget.querySelector('input')?.blur();
              }}
            >
              <Calendar
                ref={pickerRef}
                locale={i18n.language}
                className="timeslot-calendar"
                value={currentDay.isValid() ? currentDay.toDate() : null}
                dateFormat={longDateFormat}
                onChange={e => handlePickedDate(e.value as Date | null)}
                iconPos="left"
                icon={<SvgCalendar />}
                showIcon
              />
            </div>

            <RingilButton
              mode="table"
              icon={<SvgChevron className="h-4 w-4 -rotate-90" />}
              onClick={() => onDateChange(dayjs(currentDate).add(1, 'days').format())}
              tooltip={tr('TimeslotReservations.NextDay', 'Next day')}
              className={`${CONTROL_CLASS} rounded-none rounded-r px-3`}
              dataTestId="calendar-next-day"
            />
          </div>

          <RingilButton
            mode="table"
            label={tr('TimeslotReservations.Today', 'Today')}
            onClick={() => onDateChange(dayjs().format())}
            className={`${CONTROL_CLASS} rounded px-3`}
            dataTestId="calendar-today"
          />
        </div>

        <RingilButton
          mode="link"
          icon={<SvgPlus className="shrink-0" />}
          label={tr('calendar.newReservation', 'New reservation')}
          onClick={onNewReservation}
          className="h-8 gap-2.5 p-0 pr-4 text-base font-normal"
          dataTestId="calendar-new-reservation"
        />
      </div>

      <RingilButton
        mode="table"
        // plan.svg hard-codes its fill; recolour it to the button's text colour.
        icon={<SvgPlan className="h-4 w-4 [&_path]:fill-current" />}
        label={tr('TimeslotReservations.Customise', 'Customise')}
        onClick={onCustomise}
        className={`${CONTROL_CLASS} rounded px-3`}
        dataTestId="calendar-customise"
      />
    </div>
  );
};